/**
 * React Query хук для загрузки вишлистов из Supabase
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib';
import {
  parseError,
  wishlistQueryKeys,
  handleApiError,
  safeUuid,
  inFilterUuid,
} from '@/lib';
import type { Wishlist, DbWishlist, DbWishlistItem } from '../model';
import { mapDbWishlistToDomain } from '../lib';

export function useWishlistsQuery(userId?: string, showOnlyPublic = false): UseQueryResult<Wishlist[], Error> {
  return useQuery({
    queryKey: wishlistQueryKeys.wishlists(userId, showOnlyPublic),
    queryFn: async (): Promise<Wishlist[]> => {
      try {
        // Загружаем все колонки вишлистов (нужно для mapDbWishlistToDomain)
        let query = supabase
          .from('wishlists')
          .select('*')
          .order('created_at', { ascending: false });

        // Если указан userId - фильтруем по нему
        if (userId) {
          // Получаем ID вишлистов, где пользователь — принятый соавтор
          const { data: collabData } = await supabase
            .from('wishlist_collaborators')
            .select('wishlist_id')
            .eq('user_id', userId)
            .eq('status', 'accepted');

          const collabWishlistIds = (collabData || []).map(c => c.wishlist_id);

          // Защита от PostgREST injection (I54):
          //   - safeUuid валидирует userId и оборачивает в двойные кавычки.
          //   - inFilterUuid валидирует массив UUID соавтора, возвращает null если пусто.
          // См. postgrestFilters.ts.
          const safeUser = safeUuid(userId);
          const inClause = inFilterUuid('id', collabWishlistIds);

          if (showOnlyPublic) {
            // Для чужих пользователей: их публичные вишлисты + публичные коллаборативные
            if (inClause) {
              query = query
                .eq('privacy', 'public')
                .or(`user_id.eq.${safeUser},${inClause}`);
            } else {
              query = query.eq('user_id', userId).eq('privacy', 'public');
            }
          } else {
            // Для своего профиля: собственные вишлисты + коллаборативные (любой приватности)
            if (inClause) {
              query = query.or(`user_id.eq.${safeUser},${inClause}`);
            } else {
              query = query.eq('user_id', userId);
            }
          }
        }

        const { data: wishlistsData, error: wishlistsError } = await query;

        if (wishlistsError) {
          handleApiError(wishlistsError, { consolePrefix: 'Ошибка загрузки вишлистов' });
          const appError = parseError(wishlistsError);
          throw appError;
        }

        // Загружаем items для всех вишлистов (только нужные поля для карточек)
        const wishlistIds = (wishlistsData || []).map(w => w.id);
        
        const items: DbWishlistItem[] = wishlistIds.length > 0
          ? await (async () => {
              const { data: itemsData, error: itemsError } = await supabase
                .from('wishlist_items')
                .select('*')
                .in('wishlist_id', wishlistIds);

              if (itemsError) {
                handleApiError(itemsError, { consolePrefix: 'Ошибка загрузки желаний вишлиста' });
                // Не бросаем ошибку, просто items будет пустым
                return [];
              }
              
              return itemsData || [];
            })()
          : [];

        // Преобразуем данные из БД в формат приложения
        return (wishlistsData || []).map((item: DbWishlist) => mapDbWishlistToDomain(item, items));
      } catch (error) {
        // Преобразуем любую ошибку в AppError если еще не преобразовали
        const appError = parseError(error);
        throw appError;
      }
    },
    enabled: true, // Включен всегда, но можно управлять через параметр
    retry: (failureCount, error) => {
      // Повторяем только сетевые ошибки
      const appError = parseError(error);
      if (appError.code === 'NETWORK_ERROR' || appError.code === 'TIMEOUT') {
        return failureCount < 2; // Максимум 2 попытки
      }
      return false; // Не повторяем другие ошибки
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000), // Exponential backoff
  });
}